import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Box, Button, CircularProgress, FormControl, Grid, Input, Option, Select, SvgIcon, Table } from "@mui/joy";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import React from "react";
import { useDebounce } from "usehooks-ts";
import InspectionDialog from "../Dialogs/InspectionDialog";
import { Util } from "../Helpers/Util";
import { Elevator } from "../Interfaces/Elevator";
import { Mechanic } from "../Interfaces/Mechanic";
import { Building } from "./BuildingList";

export interface Inspection {
	BuildingName: string;
	ElevatorsID: string;
	SerialNumber: string;
	Passed?: boolean;
	FirstDate?: Date | null;
	SecondDate?: Date | null;
	ThirdDate?: Date | null;
	FourthDate?: Date | null;
	FifthDate?: Date | null;
	SixthDate?: Date | null;
	SeventhDate?: Date | null;
	EighthDate?: Date | null;
	NinthDate?: Date | null;
	TenthDate?: Date | null;
	ExpirationDate?: Date | null;
	Modified: boolean;
	InspectionsID: string;
	SecondPassed?: boolean;
	ThirdPassed?: boolean;
	FourthPassed?: boolean;
	FifthPassed?: boolean;
	SixthPassed?: boolean;
	SeventhPassed?: boolean;
	EighthPassed?: boolean;
	NinthPassed?: boolean;
	TenthPassed?: boolean;
	ID: string;
	MechanicID: string;
	Company: string;
	BuildingID: string;
	MechanicName: string;
	Notes: string;
	LastInspectionDate?: Date;
	Status: string;
	Alteration: boolean;
	Annual: boolean;
}

interface InspectionsListProps {
	annual: boolean;
	alterations: boolean;
}

export default function InspectionsList(props: InspectionsListProps) {
	const [buildings, setBuildings] = React.useState<Array<Building>>();
	const [mechanics, setMechanics] = React.useState<Array<Mechanic>>();
	const [elevators, setElevators] = React.useState<Array<Elevator>>();
	const [companies, setCompanies] = React.useState<Array<string>>();
	const [inspections, setInspections] = React.useState<Array<Inspection>>();
	const [globalFilter, setGlobalFilter] = React.useState("" as string);
	const globalFilterD = useDebounce<string>(globalFilter, 300);
	const [iid, setIid] = React.useState<Inspection>();
	const [dialog, setDialog] = React.useState(false);
	const [years, setYears] = React.useState([] as Array<number>);
	const [yearFilter, setYearFilter] = React.useState<any>(0);
	const [orderBy, setOrderBy] = React.useState(0);
	const [order, setOrder] = React.useState(false);

	React.useEffect(() => {
		getData();
		!elevators && getElevators();
		!mechanics && getMechanics();
		!buildings && getBuildings();
		!companies && getCompanyList();
		!years.length && getYears();
	}, [orderBy, order, globalFilterD, yearFilter]);

	const getElevators = () => {
		fetch("Building/GetAllElevators", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({}),
		})
			.then((x) => x.json())
			.then((x) => setElevators(x));
	};

	const getMechanics = () => {
		fetch("User/GetMechanics", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({}),
		})
			.then((x) => x.json())
			.then((x) => setMechanics(x));
	};

	const getBuildings = () => {
		fetch("Building")
			.then((x) => x.json())
			.then((x) => {
				setBuildings(
					x
						.sort((a: any, b: any) => a.building_name.localeCompare(b.building_name))
						.map((y: any) => {
							return { Name: y.building_name, ID: y.buildings_id } as Building;
						})
				);
			});
	};

	const getData = () => {
		const ob = ["building_name", "serial_number", "mechanic_name", "company", "", "expiration_date", "last_inspection_date"];
		const orderFilter = [];
		if (ob[orderBy] !== "") {
			orderFilter.push({ ColumnName: ob[orderBy], IsDescending: order });
		} else {
			orderFilter.push({ ColumnName: ob[0], IsDescending: order });
		}
		fetch("Inspection/GetInspectionList", {
			method: "Post",
			headers: {
				"Content-Type": "application/json",
			},
			cache: "no-cache",
			body: JSON.stringify({ approved: true, annual: props.annual, year: yearFilter, orderBy: orderFilter, globalFilter: globalFilter, alterations: props.alterations }),
		})
			.then((x) => x.json())
			.then((x) => {
				setInspections(
					x.map((y: any) => {
						const i = {
							BuildingName: y.building_name,
							ElevatorsID: y.elevators_id,
							SerialNumber: y.serial_number,
							Passed: y.is_passed,
							FirstDate: Util.ParseDate(y.first_inspection),
							SecondDate: Util.ParseDate(y.second_inspection),
							ThirdDate: Util.ParseDate(y.third_inspection),
							ExpirationDate: Util.ParseDate(y.expiration_date),
							InspectionsID: y.inspections_id,
							SecondPassed: y.is_second_passed,
							ThirdPassed: y.is_third_passed,
							FourthPassed: y.is_fourth_passed,
							MechanicID: y.mechanic_id,
							Company: y.company,
							ID: Math.random().toString().substring(2),
							BuildingID: y.buildings_id,
							MechanicName: y.mechanic_name,
							FifthPassed: y.is_fifth_passed,
							SixthPassed: y.is_sixth_passed,
							SeventhPassed: y.is_seventh_passed,
							EighthPassed: y.is_eighth_passed,
							NinthPassed: y.is_ninth_passed,
							TenthPassed: y.is_tenth_passed,
							FourthDate: Util.ParseDate(y.fourth_inspection),
							FifthDate: Util.ParseDate(y.fifth_inspection),
							SixthDate: Util.ParseDate(y.sixth_inspection),
							SeventhDate: Util.ParseDate(y.seventh_inspection),
							EighthDate: Util.ParseDate(y.eighth_inspection),
							NinthDate: Util.ParseDate(y.ninth_inspection),
							TenthDate: Util.ParseDate(y.tenth_inspection),
							LastInspectionDate: Util.ParseDate(y.last_inspection_date),
							Alteration: y.is_alteration,
							Notes: y.notes,
						} as Inspection;
						i.Status = getStatus(i);

						return i;
					})
				);
			});
	};

	const getCompanyList = () => {
		fetch("Inspection/GetInspectionCompanyList", {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
			cache: "no-cache",
		})
			.then((x) => x.json())
			.then((x) => {
				setCompanies(x);
			});
	};

	const getYears = () => {
		fetch("Inspection/GetInspectionYears", {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
			cache: "no-cache",
		})
			.then((x) => x.json())
			.then((x) => {
				setYears(x);
			});
	};

	const getStatus = (x: Inspection) => {
		return x.Passed && x.FirstDate ? "First Inspection Passed" : x.FirstDate && x.SecondDate ? (x.SecondPassed ? "Second Inspection Passed" : x.ThirdPassed ? "Third Inspection Passed" : x.ThirdDate ? "Third Inspection Failed" : "Second Inspection Failed") : x.Passed === false ? "First Inspection Failed" : "";
	};

	const getResults = () => {
		if (orderBy === 4) {
			return inspections?.sort((a, b) => (order ? 1 : -1) * a.Status.localeCompare(b.Status));
		}
		return inspections;
	};

	const renderGrid = () => {
		if (!mechanics) return <></>;
		if (!buildings) return <></>;
		if (!elevators) return <></>;
		if (!companies) return <></>;
		const cols = ["Building", "Serial Number", "Mechanic", "Company", "Inspection Status", "Expiration Date", "Last Inspection"];
		return (
			<LocalizationProvider dateAdapter={AdapterDateFns}>
				{dialog && <InspectionDialog
					annual={props.annual}
					alteration={props.alterations}
					close={() => {
						setDialog(false);
						getCompanyList();
						getData();
					}}
					open={dialog}
					insp={iid}
					buildings={buildings}
					elevators={elevators}
					mechanics={mechanics}
					companies={companies}
				/>}
				<Grid container>
					<Button
						sx={{ marginTop: 1 }}
						onClick={() => {
							setIid({} as Inspection);
							setDialog(true);
						}}>
						Add Inspection
					</Button>
					<Select
						size="sm"
						sx={{ m: 1 }}
						value={yearFilter}
						onChange={(event, value) => {
							setYearFilter(value);
							getData();
						}}>
						<Option value={0}>All Years</Option>
						{years.map((x) => (
							<Option value={x}>{x}</Option>
						))}
					</Select>
					<FormControl sx={{ ml: 1 }}>
						<Input
							name="Search"
							placeholder="Global Search"
							onChange={(event) => {
								setGlobalFilter(event.target.value);
							}}
							value={globalFilter}
						/>
					</FormControl>
				</Grid>

				<Table stripe="even" hoverRow>
					<thead>
						<tr>
							{cols.map((x, idx) => (
								<th
									style={{ cursor: idx <= 3 ? "pointer" : "inherit" }}
									onClick={() => {
										setOrderBy(idx);
										setOrder(orderBy === idx ? !order : true);
									}}>
									{x}
									{orderBy === idx ? (
										<SvgIcon
											onClick={() => {
												setOrder(!order);
												getData();
											}}
											component={order ? ArrowDownwardIcon : ArrowUpwardIcon}
											sx={{ cursor: "pointer", position: "absolute" }}
										/>
									) : (
										<></>
									)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{getResults()?.map((x) => (
							<tr
								onClick={() => {
									setIid(x);
									setDialog(true);
								}}>
								<td>{x.BuildingName}</td>
								<td>{x.SerialNumber}</td>
								<td>{x.MechanicName}</td>
								<td>{x.Company}</td>
								<td>{x.Status}</td>
								<td>{Util.Formatter(x.ExpirationDate, "D")}</td>
								<td>{Util.Formatter(x.LastInspectionDate, "D")}</td>
							</tr>
							// <InspectionRow inspection={x} mechanics={mechanics} buildings={buildings} elevators={elevators} />
						))}
					</tbody>
				</Table>
			</LocalizationProvider>
		);
	};

	const notLoading = elevators && buildings && mechanics && inspections;
	return !notLoading ? (
		<Box sx={{ display: "flex", justifyContent: "center" }}>
			<CircularProgress />
		</Box>
	) : (
		renderGrid()
	);
}
